import React from "react";
import { CalendarClock, Clock3, Info, Save } from "lucide-react";
import { toast } from "sonner";
import { useGet, apiPut } from "../../hooks/useApi.js";

type SchedulingOverride = {
  id?: string;
  board?: string;
  type?: string;
  defaultDurationMinutes: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
};
type S = {
  companyName: string;
  timezone: string;
  businessDays: number[];
  businessStart: string;
  businessEnd: string;
  dailyCapacityHours: number;
  afterHoursPolicy: "on_call" | "next_business_day";
  emergencyPriority: number;
  defaultDurationMinutes?: number;
  minDurationMinutes?: number;
  maxDurationMinutes?: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  leadTimeMinutes?: number;
  schedulingHorizonDays?: number;
  slotIncrementMinutes?: number;
  schedulingOverrides?: SchedulingOverride[];
};
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const defaults = {
  defaultDurationMinutes: 60,
  minDurationMinutes: 15,
  maxDurationMinutes: 480,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 15,
  leadTimeMinutes: 60,
  schedulingHorizonDays: 30,
  slotIncrementMinutes: 15,
};

export function CompanySettingsCard({ editable }: { editable: boolean }) {
  const q = useGet<S>("/api/v1/company-settings");
  const [v, setV] = React.useState<S | null>(null);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (q.data) {
      const quarter = (minutes: number) => Math.round(minutes / 15) * 15;
      setV({
        ...defaults,
        ...q.data,
        dailyCapacityHours: Math.round(q.data.dailyCapacityHours * 4) / 4,
        defaultDurationMinutes: quarter(q.data.defaultDurationMinutes ?? defaults.defaultDurationMinutes),
        minDurationMinutes: quarter(q.data.minDurationMinutes ?? defaults.minDurationMinutes),
        maxDurationMinutes: quarter(q.data.maxDurationMinutes ?? defaults.maxDurationMinutes),
        bufferBeforeMinutes: quarter(q.data.bufferBeforeMinutes ?? defaults.bufferBeforeMinutes),
        bufferAfterMinutes: quarter(q.data.bufferAfterMinutes ?? defaults.bufferAfterMinutes),
        leadTimeMinutes: quarter(q.data.leadTimeMinutes ?? defaults.leadTimeMinutes),
        slotIncrementMinutes: quarter(q.data.slotIncrementMinutes ?? defaults.slotIncrementMinutes),
        schedulingOverrides: (q.data.schedulingOverrides ?? []).map((item) => ({
          ...item,
          defaultDurationMinutes: quarter(item.defaultDurationMinutes),
          bufferBeforeMinutes: quarter(item.bufferBeforeMinutes ?? 0),
          bufferAfterMinutes: quarter(item.bufferAfterMinutes ?? 0),
        })),
      });
    }
  }, [q.data]);
  if (q.error)
    return (
      <section className="rounded-2xl border border-red-300 p-5">
        <h2 className="font-semibold">Scheduling settings unavailable</h2>
        <p className="mt-1 text-sm text-red-600">{String(q.error)}</p>
        <button onClick={q.refetch} className="mt-3 rounded-lg border px-3 py-2 text-sm">
          Retry
        </button>
      </section>
    );
  if (!v)
    return (
      <div className="rounded-2xl border p-5" role="status">
        Loading company settings…
      </div>
    );
  const set = <K extends keyof S>(k: K, x: S[K]) => setV({ ...v, [k]: x });
  const n = (k: keyof S, x: string) => set(k, Number(x) as never);
  const save = async () => {
    setSaving(true);
    try {
      await apiPut("/api/v1/company-settings", v);
      toast.success("Scheduling rules saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save scheduling rules");
    } finally {
      setSaving(false);
    }
  };
  return (
    <section
      className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden"
      aria-labelledby="scheduling-settings-title"
    >
      <div className="border-b border-[hsl(var(--border))] px-5 py-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-brand-600" />
          <h2 id="scheduling-settings-title" className="font-semibold">
            Availability & scheduling
          </h2>
        </div>
        <p className="mt-1 text-xs text-[hsl(var(--content-subtle))]">
          Controls when work can be offered. ConnectWise calendar blocks, time off, capacity,
          buffers and on-call coverage are applied before a slot is shown.
        </p>
      </div>
      <div className="space-y-6 p-5">
        <fieldset disabled={!editable} className="space-y-4 disabled:opacity-70">
          <legend className="sr-only">Company hours</legend>
          <label className="block text-sm font-medium">
            Company name
            <input
              value={v.companyName}
              onChange={(e) => set("companyName", e.target.value)}
              className="mt-1 w-full rounded-lg border bg-transparent p-2"
            />
          </label>
          <label className="block text-sm font-medium">
            Scheduling timezone
            <input
              value={v.timezone}
              onChange={(e) => set("timezone", e.target.value)}
              aria-describedby="timezone-help"
              className="mt-1 w-full rounded-lg border bg-transparent p-2"
            />
            <span
              id="timezone-help"
              className="mt-1 block text-xs text-[hsl(var(--content-subtle))]"
            >
              IANA timezone used for daylight-saving-safe business hours, for example
              America/Chicago.
            </span>
          </label>
          <div>
            <span className="text-sm font-medium">Working days</span>
            <div className="mt-2 flex flex-wrap gap-1" role="group" aria-label="Working days">
              {DAYS.map((d, i) => (
                <button
                  type="button"
                  aria-pressed={v.businessDays.includes(i)}
                  key={d}
                  onClick={() =>
                    set(
                      "businessDays",
                      v.businessDays.includes(i)
                        ? v.businessDays.filter((x) => x !== i)
                        : [...v.businessDays, i].sort(),
                    )
                  }
                  className={
                    "min-w-10 rounded-lg border px-2 py-1.5 text-xs " +
                    (v.businessDays.includes(i)
                      ? "border-brand-600 bg-brand-600 text-white"
                      : "border-[hsl(var(--border))]")
                  }
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Workday starts"
              type="time"
              value={v.businessStart}
              onChange={(x) => set("businessStart", x)}
            />
            <Field
              label="Workday ends"
              type="time"
              value={v.businessEnd}
              onChange={(x) => set("businessEnd", x)}
            />
            <NumberField
              label="Daily capacity"
              suffix="hours"
              value={v.dailyCapacityHours}
              min={0.25}
              step={0.25}
              max={24}
              onChange={(x) => n("dailyCapacityHours", x)}
            />
            <NumberField
              label="Emergency priority"
              value={v.emergencyPriority}
              min={1}
              max={5}
              onChange={(x) => n("emergencyPriority", x)}
            />
          </div>
          <label className="block text-sm font-medium">
            After-hours handling
            <select
              value={v.afterHoursPolicy}
              onChange={(e) => set("afterHoursPolicy", e.target.value as S["afterHoursPolicy"])}
              className="mt-1 w-full rounded-lg border bg-[hsl(var(--surface))] p-2"
            >
              <option value="on_call">Offer emergency work to on-call technicians</option>
              <option value="next_business_day">Offer the next valid business-day slot</option>
            </select>
          </label>
        </fieldset>
        <div className="border-t border-[hsl(var(--border))] pt-5">
          <div className="mb-4 flex gap-2">
            <Clock3 className="mt-0.5 h-4 w-4" />
            <div>
              <h3 className="text-sm font-semibold">Appointment rules</h3>
              <p className="text-xs text-[hsl(var(--content-subtle))]">
                AI may estimate duration, but every suggestion is rounded and clamped to these
                deterministic limits.
              </p>
            </div>
          </div>
          <fieldset disabled={!editable} className="grid gap-3 sm:grid-cols-2 disabled:opacity-70">
            <legend className="sr-only">Appointment rules</legend>
            <NumberField
              label="Default length"
              suffix="hours"
              minutes
              value={v.defaultDurationMinutes!}
              min={0.25}
              onChange={(x) => n("defaultDurationMinutes", x)}
            />
            <NumberField
              label="Slot increments"
              suffix="hours"
              minutes
              value={v.slotIncrementMinutes!}
              min={0.25}
              onChange={(x) => n("slotIncrementMinutes", x)}
            />
            <NumberField
              label="Minimum length"
              suffix="hours"
              minutes
              value={v.minDurationMinutes!}
              min={0.25}
              onChange={(x) => n("minDurationMinutes", x)}
            />
            <NumberField
              label="Maximum length"
              suffix="hours"
              minutes
              value={v.maxDurationMinutes!}
              min={0.25}
              onChange={(x) => n("maxDurationMinutes", x)}
            />
            <NumberField
              label="Buffer before"
              suffix="hours"
              minutes
              value={v.bufferBeforeMinutes!}
              min={0}
              onChange={(x) => n("bufferBeforeMinutes", x)}
            />
            <NumberField
              label="Buffer after"
              suffix="hours"
              minutes
              value={v.bufferAfterMinutes!}
              min={0}
              onChange={(x) => n("bufferAfterMinutes", x)}
            />
            <NumberField
              label="Minimum lead time"
              suffix="hours"
              minutes
              value={v.leadTimeMinutes!}
              min={0}
              onChange={(x) => n("leadTimeMinutes", x)}
            />
            <NumberField
              label="Booking horizon"
              suffix="working days"
              value={v.schedulingHorizonDays!}
              min={1}
              max={365}
              onChange={(x) => n("schedulingHorizonDays", x)}
            />
          </fieldset>
        </div>
        <Overrides
          editable={editable}
          value={v.schedulingOverrides ?? []}
          onChange={(x) => set("schedulingOverrides", x)}
        />
        {!editable && (
          <p className="flex gap-2 rounded-lg bg-[hsl(var(--surface-subtle))] p-3 text-xs text-[hsl(var(--content-subtle))]">
            <Info className="h-4 w-4 shrink-0" />
            Only workspace admins and owners can change scheduling rules.
          </p>
        )}
        {editable && (
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : "Save scheduling rules"}
          </button>
        )}
      </div>
    </section>
  );
}
function Field({
  label,
  type,
  value,
  onChange,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (x: string) => void;
}) {
  return (
    <label className="text-sm font-medium">
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border bg-transparent p-2"
      />
    </label>
  );
}
function NumberField({
  label,
  suffix,
  value,
  min,
  max,
  step,
  minutes = false,
  onChange,
}: {
  label: string;
  suffix?: string;
  value: number;
  min: number;
  max?: number;
  step?: number;
  minutes?: boolean;
  onChange: (x: string) => void;
}) {
  return (
    <label className="text-sm font-medium">
      {label}
      <span className="relative mt-1 block">
        <input
          type="number"
          value={minutes ? value / 60 : value}
          min={min}
          max={max}
          step={step ?? (minutes ? 0.25 : undefined)}
          onChange={(e) => onChange(String(Number(e.target.value) * (minutes ? 60 : 1)))}
          className="w-full rounded-lg border bg-transparent p-2 pr-24"
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3 top-2.5 text-xs text-[hsl(var(--content-subtle))]">
            {suffix}
          </span>
        )}
      </span>
    </label>
  );
}
function Overrides({
  editable,
  value,
  onChange,
}: {
  editable: boolean;
  value: SchedulingOverride[];
  onChange: (x: SchedulingOverride[]) => void;
}) {
  const add = () =>
    onChange([
      ...value,
      {
        board: "",
        type: "",
        defaultDurationMinutes: 60,
        bufferBeforeMinutes: 0,
        bufferAfterMinutes: 15,
      },
    ]);
  return (
    <div className="border-t border-[hsl(var(--border))] pt-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Board or ticket-type overrides</h3>
          <p className="text-xs text-[hsl(var(--content-subtle))]">
            Optional. More specific rules replace the default appointment length and buffers.
          </p>
        </div>
        {editable && (
          <button type="button" onClick={add} className="rounded-lg border px-3 py-1.5 text-xs">
            Add override
          </button>
        )}
      </div>
      <div className="mt-3 space-y-3">
        {value.map((o, i) => (
          <fieldset
            disabled={!editable}
            key={o.id ?? i}
            className="grid gap-2 rounded-xl border border-[hsl(var(--border))] p-3 sm:grid-cols-5"
          >
            <legend className="sr-only">Scheduling override {i + 1}</legend>
            <Field
              label="Board"
              type="text"
              value={o.board ?? ""}
              onChange={(x) => onChange(value.map((a, j) => (j === i ? { ...a, board: x } : a)))}
            />
            <Field
              label="Ticket type"
              type="text"
              value={o.type ?? ""}
              onChange={(x) => onChange(value.map((a, j) => (j === i ? { ...a, type: x } : a)))}
            />
            <NumberField
              label="Length"
              value={o.defaultDurationMinutes}
              min={0.25}
              step={0.25}
              minutes
              suffix="hours"
              onChange={(x) =>
                onChange(
                  value.map((a, j) => (j === i ? { ...a, defaultDurationMinutes: Number(x) } : a)),
                )
              }
            />
            <NumberField
              label="Before"
              value={o.bufferBeforeMinutes ?? 0}
              min={0}
              step={0.25}
              minutes
              suffix="hours"
              onChange={(x) =>
                onChange(
                  value.map((a, j) => (j === i ? { ...a, bufferBeforeMinutes: Number(x) } : a)),
                )
              }
            />
            <div>
              <NumberField
                label="After"
                value={o.bufferAfterMinutes ?? 0}
                min={0}
                step={0.25}
                minutes
                suffix="hours"
                onChange={(x) =>
                  onChange(
                    value.map((a, j) => (j === i ? { ...a, bufferAfterMinutes: Number(x) } : a)),
                  )
                }
              />
              {editable && (
                <button
                  type="button"
                  onClick={() => onChange(value.filter((_, j) => j !== i))}
                  className="mt-2 text-xs text-red-600"
                >
                  Remove override
                </button>
              )}
            </div>
          </fieldset>
        ))}
        {value.length === 0 && (
          <p className="rounded-lg bg-[hsl(var(--surface-subtle))] p-3 text-xs text-[hsl(var(--content-subtle))]">
            No overrides. All tickets use the default rules above.
          </p>
        )}
      </div>
    </div>
  );
}
